import { useTranslation } from 'react-i18next';
import { localizedText, metricReferences } from '../types';
import { EditorModal } from './EditorModal';
import { LibraryRow, SectionTitle, StatusBadge } from './editorStyles';
import { Action, Bar, Hint, QuietAction } from './styles';
import type { OverviewEditor } from './useOverviewEditor';

/** Every metric of the report, shown or hidden. Opened from the dock, or from a gap with an insertion index. */
export function MetricLibraryModal({
  editor,
  index,
  onClose,
}: {
  editor: OverviewEditor;
  index?: number;
  onClose: () => void;
}) {
  const { t } = useTranslation('newReport');
  const { document, components, controller, language } = editor;
  const placementOf = (metricId: string) =>
    components.find((component) => component.type === 'metric' && component.metricId === metricId);
  const tablesUsing = (metricId: string) =>
    components.filter(
      (component) =>
        component.type === 'table' &&
        component.table.columns.some((column) => 'metricId' in column && column.metricId === metricId),
    ).length;
  const referenceLabel = (id: string) =>
    localizedText(document.metrics.find((metric) => metric.id === id)?.title, language) ||
    (components.find((component) => component.id === id)?.type === 'table'
      ? t('definition.componentTypes.table')
      : t('definition.componentTypes.metric'));
  return (
    <EditorModal
      size="large"
      title={t('definition.metricsLibrary')}
      onClose={onClose}
      footer={
        <>
          <Hint style={{ flex: 1 }}>{t('definition.metricsLibraryHelp')}</Hint>
          <Action onClick={onClose}>{t('definition.close')}</Action>
          <Action primary onClick={() => editor.createMetric(index)}>
            + {t('definition.createMetric')}
          </Action>
        </>
      }
    >
      {index !== undefined && editor.unplacedRates.length > 0 && (
        <>
          <SectionTitle>{t('definition.builtInMetrics')}</SectionTitle>
          {editor.unplacedRates.map((type) => (
            <LibraryRow key={type}>
              <div>
                <strong>{t(`definition.${type}`)}</strong>
                <Hint>{t(`definition.${type}Help`)}</Hint>
              </div>
              <Bar>
                <Action
                  primary
                  onClick={() => {
                    editor.placeRate(type, index);
                    onClose();
                  }}
                >
                  {t('definition.addHere')}
                </Action>
              </Bar>
            </LibraryRow>
          ))}
          <SectionTitle>{t('definition.yourMetrics')}</SectionTitle>
        </>
      )}
      {!document.metrics.length && <Hint>{t('definition.noMetrics')}</Hint>}
      {document.metrics.map((metric) => {
        const placement = placementOf(metric.id);
        const tables = tablesUsing(metric.id);
        const error = controller.metricErrors[metric.id];
        const references = metricReferences(document, metric.id);
        return (
          <LibraryRow key={metric.id}>
            <div>
              <strong>{localizedText(metric.title, language) || t('definition.newMetric')}</strong>
              <Hint>
                {t(`definition.calculations.${metric.calculation.type}`)} ·{' '}
                {metric.identifier && <>{metric.identifier} · </>}
                {error ? (
                  <StatusBadge tone="warn">{t('definition.needsSetup')}</StatusBadge>
                ) : placement ? (
                  <StatusBadge tone="positive">{t('definition.placed')}</StatusBadge>
                ) : (
                  <StatusBadge>
                    {t(tables ? 'definition.hiddenUsedInTables' : 'definition.hiddenUnused', { count: tables })}
                  </StatusBadge>
                )}
              </Hint>
            </div>
            <Bar>
              {!placement && (
                <Action
                  primary
                  disabled={!!error}
                  onClick={() => {
                    editor.placeMetric(metric.id, index);
                    onClose();
                  }}
                >
                  {t(index === undefined ? 'definition.addToReport' : 'definition.addHere')}
                </Action>
              )}
              <Action onClick={() => editor.openMetric(metric.id, { placementId: placement?.id })}>
                {t('definition.edit')}
              </Action>
              <QuietAction
                disabled={references.length > 0}
                title={
                  references.length
                    ? t('definition.inUse', { references: references.map(referenceLabel).join(', ') })
                    : t('definition.deleteMetric')
                }
                onClick={() => {
                  if (window.confirm(t('definition.deleteMetricConfirm'))) editor.removeMetric(metric.id);
                }}
              >
                {t('definition.deleteMetric')}
              </QuietAction>
            </Bar>
          </LibraryRow>
        );
      })}
    </EditorModal>
  );
}
